import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { AlertColor, Button, Divider, Grid, IconButton, useMediaQuery } from '@mui/material';
import { Stack, useTheme } from '@mui/system';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import Form, { FormDataModel } from 'components/Form';
import Section from 'components/Section/Section';
import dayjs from 'dayjs';
import React, { Dispatch, MouseEvent, RefObject, SetStateAction, useRef, useState } from 'react';
import { getDrawerWidth, removeErrorFieldsFromValues } from 'utils/validators/HelperFunctions';
import { AddEditCorporateRateForm } from './AddEditCorporateRateForm';
import RoomTypeVsCorporateRate from './RoomTypeVsCorporateRate';
import TimeslotRateMapping from './TimeslotRateMapping';
import { Validators } from '../../../../../../utils/validators';
import InputField from '../../../../../../components/InputField';
import SendCorporateRate from '../../SendCorporateRate/SendCorporateRate';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GetAppIcon from '@mui/icons-material/GetApp';
import Tooltip from '@mui/material/Tooltip';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import ConfirmationModal from 'components/ConfirmationModal';
import Upload from 'components/Upload/Upload';

type CorporateRateInfoProps = {
  companyId: number;
  branchId: number;
  assigneeList: any;
  setMessage: Dispatch<
    SetStateAction<{
      display: boolean;
      severity: AlertColor | null;
      message: any;
    }>
  >;
  openDrawer: any;
  setOpenDrawer: any;
  corporateRates: any;
  setCorporateRates: any;
  hideAddButton?: boolean;
};
const CorporateRateInfo = ({
  companyId,
  branchId,
  assigneeList,
  setMessage,
  openDrawer,
  setOpenDrawer,
  corporateRates,
  setCorporateRates,
  hideAddButton = false,
}: CorporateRateInfoProps) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditDrawer, setIsEditDrawer] = useState<boolean>(false);
  const [corporateRatesPageResponse, setCorporateRatesPageResponse] = useState<any>({});
  const [hasError, setHasError] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const [roomTypeVsCorporateRate, setRoomTypeVsCorporateRate] = useState<any>([]);
  const [timeslotRateMapping, setTimeslotRateMapping] = useState<any>([]);
  const [corporateRateInclusion, setCorporateRateInclusion] = useState('');
  const [error, setError] = useState<any>(null);
  const [openSendEmailDrawer, setOpenSendEmailDrawer] = useState<boolean>(false);
  const [rateId, setRateId] = useState<any>();
  const [openUploadDrawer, setOpenUploadDrawer] = useState<boolean>(false);
  const [uploadRateId, setUploadRateId] = useState<any>(null);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [contractSigned, setContractSigned] = useState<boolean>(false);
  let formRef: RefObject<Form | null | undefined> = useRef();

  const handleClose = () => {
    setOpenDrawer(false);
    setCorporateRatesPageResponse(null);
    setRoomTypeVsCorporateRate(null);
    setTimeslotRateMapping(null);
    formRef.current?.resetForm();
    setIsEditDrawer(false);
    // setSelectedRowId(null);
    setHasError(false);
    setCorporateRateInclusion('');
    setError(null);
  };
  const handleSave = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    let myData = removeErrorFieldsFromValues(formData);
    const commentValidation = validateComment(corporateRateInclusion);
    const isCommentValid = commentValidation && commentValidation?.error;
    setHasError(false);

    if (isFormValid && !isCommentValid) {
      if (dayjs(myData.validTo).isBefore(dayjs(myData.validFrom))) {
        setMessage({
          display: true,
          severity: 'error',
          message: 'Valid to cannot be less that valid from date',
        });
        return;
      } else {
        const data = {
          ...myData,
          validFrom: myData.validFrom,
          validTo: myData.validTo,
          roomRateMappings: roomTypeVsCorporateRate ?? [],
          timeSlotRateMappings: timeslotRateMapping ?? [],
          issuedBy: {
            label: assigneeList.find((i: any) => i.value === myData.issuedBy)?.label,
            value: myData.issuedBy,
          },
          corporateRateInclusion: corporateRateInclusion,
        };
        const array = [...corporateRates];
        let currentIndex: number;
        if (selectedRowId !== null) {
          array[selectedRowId] = { ...array[selectedRowId], ...data };
          currentIndex = selectedRowId;
          setSelectedRowId(null);
        } else {
          array.push(data);
          currentIndex = array.length - 1;
        }
        handleApiCall(array, 'edit', currentIndex);
      }
    } else {
      setHasError(true);
      setError(commentValidation);
    }
  };

  const handleApiCall = async (array: any, type: string, currentIndex?: number) => {
    try {
      const payload = array.map((rate: any) => ({
        ...rate,
        validFrom: dayjs(rate.validFrom).format('DD-MM-YYYY'),
        validTo: dayjs(rate.validTo).format('DD-MM-YYYY'),
        issuedBy: rate.issuedBy.value,
        id: rate.id,
      }));
      const { status, data } = await axiosInstance.put(URLS.updateCorporateRates(companyId), payload);
      if (status === 200 || status === 201) {
        const hasMissingIds = array.some((rate: any) => !rate.id);
        let updatedArray = array;

        if (Array.isArray(data)) {
          updatedArray = array.map((rate: any, index: number) => {
            const responseRate = data[index];
            if (responseRate && responseRate.id) {
              return {
                ...rate,
                id: responseRate.id,
              };
            }
            return rate;
          });
        } else if (data && data.id && hasMissingIds) {
          // If response is a single object with id, update the rate without id
          updatedArray = array.map((rate: any) => {
            if (!rate.id) {
              return {
                ...rate,
                id: data.id,
              };
            }
            return rate;
          });
        }

        if (type === 'edit') {
          // Set rateId for the recently created/edited rate so preview/send APIs get a valid ID
          const effectiveIndex =
            typeof currentIndex === 'number' && currentIndex >= 0 && currentIndex < updatedArray.length
              ? currentIndex
              : updatedArray.length - 1;
          const currentRate = updatedArray[effectiveIndex];
          if (currentRate?.id) {
            setRateId(currentRate.id);
          }
          setOpenSendEmailDrawer(true);
          setMessage({
            display: true,
            severity: 'success',
            message: strings.common_save_message,
          });
          setCorporateRates(updatedArray);
          setCorporateRatesPageResponse({});
          setIsEditDrawer(false);
          setCorporateRateInclusion('');
          handleClose();
        } else {
          setMessage({
            display: true,
            severity: 'success',
            message: strings.common_delete_message,
          });
          setCorporateRates(updatedArray);
          setShowDeleteModal(false);
          setSelectedRowId(null);
        }
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response?.data?.message || error.message,
      });
    }
  };
  const deleteRow = (idx: any) => {
    const deleteRow = JSON.parse(JSON.stringify(corporateRates));
    const deleteValue = deleteRow.filter((item: any, i: any) => i !== idx);
    handleApiCall(deleteValue, 'delete');
  };
  const editRow = (idx: any) => {
    setSelectedRowId(idx);
    const editValue = corporateRates.find((item: any, i: any) => i === idx);
    setCorporateRatesPageResponse({
      ...editValue,
      issuedBy: editValue.issuedBy?.value,
    });
    setRoomTypeVsCorporateRate(editValue?.roomRateMappings);
    setTimeslotRateMapping(editValue?.timeSlotRateMappings);
    setCorporateRateInclusion(editValue?.corporateRateInclusion ?? '');
    setIsEditDrawer(true);
    setOpenDrawer(true);
  };

  const sendEmail = (idx: number) => {
    const editValue = corporateRates.find((item: any, i: any) => i === idx);
    setOpenSendEmailDrawer(true);
    setRateId(editValue.id);
  };

  const openUpload = (idx: number) => {
    const editValue = corporateRates.find((item: any, i: any) => i === idx);
    setUploadRateId(editValue.id);
    setUploadedFiles(editValue?.files || []);
    setOpenUploadDrawer(true);
  };

  const handleUploadClose = () => {
    setOpenUploadDrawer(false);
    setUploadRateId(null);
    setUploadedFiles([]);
  };

  const fetchCompanyData = async () => {
    try {
      const { status, data } = await axiosInstance.get(URLS.getCompanyById(companyId));
      if (status === 200 && data?.corporateRates) {
        setCorporateRates(data.corporateRates);
      }
    } catch (error: any) {
      console.error('Error fetching company data:', error);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadRateId) {
      setMessage({
        display: true,
        severity: 'error',
        message: 'Rate ID is missing',
      });
      return;
    }
    const newFiles = uploadedFiles.filter((file: any) => file instanceof File);
    if (!newFiles || newFiles.length === 0) {
      setMessage({
        display: true,
        severity: 'error',
        message: 'Please select at least one file to upload',
      });
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      newFiles.forEach((file: File) => {
        formData.append('contractFile', file);
      });
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      const { status } = await axiosInstance.post(
        URLS.uploadCorporateRateContract(companyId, uploadRateId),
        formData,
        config,
      );
      if (status === 200 || status === 201) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.common_save_message || 'Files uploaded successfully',
        });
        handleUploadClose();
        // Fetch latest company data to update corporate rates with uploaded files
        await fetchCompanyData();
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response?.data?.message || error.message || 'Failed to upload files',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const corporateRatesTable = () => {
    const columns: GridColDef[] = [
      { field: 'validFrom', headerName: strings.validFrom, flex: 1, minWidth: 150 },
      { field: 'validTo', headerName: strings.validto, flex: 1, minWidth: 150 },
      { field: 'issuedByLabel', headerName: strings.issuedBy, flex: 1, minWidth: 150 },
      {
        field: 'Action',
        headerName: strings.customermgmt_column_action,
        sortable: false,
        flex: 1,
        minWidth: 150,
        align: 'right',
        headerAlign: 'right',
        renderCell: (ValueFormatterParams: any) => {
          const editValue = corporateRates.find((item: any, i: any) => i === ValueFormatterParams.row.id);
          const isContractSigned = editValue?.contractSigned === true;
          setContractSigned(isContractSigned);
          const handleDownload = (e: any) => {
            e.stopPropagation();
            if (editValue?.contractUrl) {
              const link = document.createElement('a');
              link.href = editValue.contractUrl;
              link.download = editValue.contractUrl.split('/').pop() || 'contract.pdf';
              link.target = '_blank';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          };

          return (
            <Stack direction="row" spacing={2}>
              {editValue.id && (
                <>
                  <Tooltip title="Send Email">
                    <IconButton
                      aria-label="send email"
                      size="small"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        sendEmail(ValueFormatterParams.row.id);
                      }}>
                      <SendOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {isContractSigned ? (
                    <Tooltip title="Download Contract">
                      <IconButton aria-label="download contract" size="small" onClick={handleDownload}>
                        <GetAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip title="Upload">
                        <IconButton
                          aria-label="upload"
                          size="small"
                          onClick={(e: any) => {
                            e.stopPropagation();
                            openUpload(ValueFormatterParams.row.id);
                          }}>
                          <UploadFileIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </>
              )}

              <IconButton
                color="info"
                aria-label="edit"
                size="small"
                onClick={(e: any) => {
                  e.stopPropagation();
                  editRow(ValueFormatterParams.row.id);
                }}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                color="error"
                aria-label="delete"
                size="small"
                onClick={(e: any) => {
                  e.stopPropagation();
                  setSelectedRowId(ValueFormatterParams.row.id);
                  setShowDeleteModal(true);
                }}>
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        },
      },
    ];

    return corporateRates?.length > 0 ? (
      <div style={{ width: '100%' }}>
        <div style={{ height: 300, width: '100%' }}>
          <DataGrid
            sx={{
              overflow: 'auto',
              '.MuiDataGrid-row': {
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: `${theme.palette.primary.main}09}`,
                },
              },
              '.MuiDataGrid-columnHeaders': {
                backgroundColor: `${theme.palette.primary.main}35}`,
              },
            }}
            rowHeight={60}
            rows={corporateRates.map((item: any, idx: any) => ({
              ...item,
              id: idx,
              validFrom: dayjs(item.validFrom).format('DD-MM-YYYY'),
              validTo: dayjs(item.validTo).format('DD-MM-YYYY'),
              issuedByLabel: assigneeList.find((i: any) => i.value === item.issuedBy.value)?.label,
            }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            disableColumnMenu
            disableSelectionOnClick
            autoHeight
            hideFooter
          />
        </div>
      </div>
    ) : null;
  };

  const handleCommentChange = (value: string) => {
    const result = validateComment(value);
    setError(result);
    setCorporateRateInclusion(value);
  };

  const validateComment = (value: string) => {
    const isMandatory: boolean | { error: boolean; message: string } = Validators.validateMandatoryField(value, '');
    if (isMandatory && isMandatory.error) {
      return isMandatory;
    }

    const isValidString:
      | {
          error: boolean;
          message: Array<string | number> | string;
        }
      | boolean = Validators.validateString(value, '', 0, 200);
    return isValidString;
  };

  const corporateRatesFormComponent = () => {
    return (
      <>
        <Grid container spacing={2} sx={{ my: 2 }}>
          <Form
            hasError={hasError}
            ref={formRef as RefObject<Form>}
            model={AddEditCorporateRateForm(strings, assigneeList)}
            values={corporateRatesPageResponse}
          />
        </Grid>
        <RoomTypeVsCorporateRate
          roomTypeVsCorporateRate={roomTypeVsCorporateRate}
          setRoomTypeVsCorporateRate={setRoomTypeVsCorporateRate}
        />
        <TimeslotRateMapping
          timeslotRateMapping={timeslotRateMapping}
          setTimeslotRateMapping={setTimeslotRateMapping}
        />
        <Grid container spacing={2} sx={{ my: 2 }}>
          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <InputField
              label={`${strings.corporateRateInclusion} *`}
              placeholder={''}
              inputValue={corporateRateInclusion}
              typeValue="textarea"
              type="text"
              validators={[{ check: Validators.validateString }]}
              rows={2}
              multiline={true}
              size={'medium'}
              variant={'outlined'}
              onChange={e => handleCommentChange(e.target.value)}
              helperText={error?.message}
              error={error?.error}
            />
          </Grid>
        </Grid>
        {!contractSigned && (
          <Grid item sx={{ my: 1 }} xs={12} sm={3} lg={2}>
            <Button variant="contained" sx={{ color: '#ffffff' }} onClick={e => handleSave(e)} fullWidth>
              {strings.common_button_save}
            </Button>
          </Grid>
        )}
      </>
    );
  };

  return (
    <>
      {!hideAddButton && (
        <>
          <Section />
          <Button variant="text" onClick={() => setOpenDrawer(true)}>
            <span style={{ marginRight: '10px' }}>{strings.lead_management_add_corporate_rate_info}</span>
            <AddCircleOutlineIcon />
          </Button>
        </>
      )}
      <SendCorporateRate
        companyId={companyId}
        rateId={rateId || selectedRowId}
        setRateId={setRateId}
        setMessage={setMessage}
        openDrawer={openSendEmailDrawer}
        setOpenDrawer={setOpenSendEmailDrawer}
      />
      <ConfirmationModal
        open={showDeleteModal}
        message={strings.confirmDeletedText}
        onCancel={() => {
          setShowDeleteModal(false);
          setSelectedRowId(null);
        }}
        onConfirm={() => {
          deleteRow(selectedRowId);
        }}
      />
      {corporateRatesTable()}
      <DrawerMenu
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd)}
        open={openDrawer}
        handleClose={handleClose}
        closeButton={true}
        title={
          isEditDrawer
            ? strings.lead_management_edit_corporate_rate_info
            : strings.lead_management_add_corporate_rate_info
        }>
        {corporateRatesFormComponent()}
      </DrawerMenu>
      <DrawerMenu
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd)}
        open={openUploadDrawer}
        handleClose={handleUploadClose}
        closeButton={true}
        title={'Upload Contract'}>
        <Grid container spacing={2} sx={{ my: 2 }}>
          <Grid item xs={12}>
            <Upload
              label={'Upload Contract'}
              inputValue={uploadedFiles}
              textChange={(value: any) => {
                setUploadedFiles(value);
              }}
              multiline={false}
              accept={['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png']}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              sx={{ color: '#ffffff' }}
              onClick={handleFileUpload}
              disabled={isUploading || !uploadedFiles || uploadedFiles.length === 0}
              fullWidth>
              Upload
            </Button>
          </Grid>
        </Grid>
      </DrawerMenu>
    </>
  );
};
export default CorporateRateInfo;
